import { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import Button1 from "../../../components/form/Button1";
import CommonFreeTextInput from "../../../components/common/inputs/CommonFreeTextInput";
import ConfirmInitiateWorldCupModal from "./ConfirmInitiateWorldCupModal";
import WorldCupActiveGameCard from "./WorldCupActiveGameCard";
import WorldCupGamesShimmer from "./WorldCupGamesShimmer";
import { getWorldCupGames } from "./worldCupController";
import {
  DEFAULT_PRIZE_POOL_AMOUNT,
  getSavedWorldCupGames,
  saveWorldCupGames,
} from "./payload";

export default function WorldCup() {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmedAmount, setConfirmedAmount] = useState(null);
  const [games, setGames] = useState(() => getSavedWorldCupGames());
  const [loadingGames, setLoadingGames] = useState(false);
  const [prizePoolAmount, setPrizePoolAmount] = useState(
    String(DEFAULT_PRIZE_POOL_AMOUNT),
  );

  const activeGame = games[0] ?? null;
  const hasGame = Boolean(activeGame);

  const loadGames = useCallback(async ({ force = false } = {}) => {
    if (!force) {
      const savedGames = getSavedWorldCupGames();
      if (savedGames.length) {
        setGames(savedGames);
        return;
      }
    }

    setLoadingGames(true);
    const res = await getWorldCupGames({ limit: 10, offset: 0 });
    setLoadingGames(false);

    if (res.return) {
      const nextGames = Array.isArray(res.data?.data) ? res.data.data : [];
      saveWorldCupGames(nextGames);
      setGames(nextGames);
      return;
    }

    if (!getSavedWorldCupGames().length) {
      enqueueSnackbar(res.msg, { variant: "error" });
    }
  }, []);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  const handlePrizePoolAmountChange = (value) => {
    setPrizePoolAmount(value.replace(/\D/g, ""));
  };

  const getValidatedAmount = () => {
    const amount = Number(prizePoolAmount);
    if (!prizePoolAmount.trim() || Number.isNaN(amount) || amount <= 0) {
      enqueueSnackbar("Please enter a valid prize pool amount", {
        variant: "error",
      });
      return null;
    }
    return amount;
  };

  const handleOpenConfirm = () => {
    const amount = getValidatedAmount();
    if (amount == null) return;
    setConfirmedAmount(amount);
    setOpenConfirm(true);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center gap-4">
        <h2 className="text-2xl font-semibold">World Cup</h2>
        <button
          type="button"
          className="flex items-center gap-2 text-sm font-medium text-primary hover:opacity-80 disabled:opacity-50"
          onClick={() => loadGames({ force: true })}
          disabled={loadingGames}
        >
          <Icon
            icon="mdi:refresh"
            className={`text-lg ${loadingGames ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>
      <hr className="border-b border-gray-200" />

      {loadingGames ? (
        <WorldCupGamesShimmer />
      ) : hasGame ? (
        <WorldCupActiveGameCard game={activeGame} />
      ) : (
        <div className="rounded-2xl border border-[#E7EDF5] bg-white p-6 flex flex-col gap-5 max-w-xl">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#E8F0FE]">
              <Icon icon="mdi:soccer" className="text-2xl text-primary" />
            </span>
            <div className="flex flex-col gap-1">
              <span className="text-lg font-semibold text-primary">
                Initiate World Cup Game
              </span>
              <span className="text-sm text-[#6D7C94]">
                Start FIFA World Cup 2026 with the configured teams and prize
                pool.
              </span>
            </div>
          </div>

          <CommonFreeTextInput
            label="Prize Pool Amount"
            placeholder={String(DEFAULT_PRIZE_POOL_AMOUNT)}
            value={prizePoolAmount}
            onChange={handlePrizePoolAmountChange}
          />

          <Button1 onClick={handleOpenConfirm}>Initiate Game</Button1>
        </div>
      )}

      <ConfirmInitiateWorldCupModal
        open={openConfirm}
        setOpen={setOpenConfirm}
        prizePoolAmount={confirmedAmount}
        onSuccess={() => loadGames({ force: true })}
      />
    </div>
  );
}
